import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBagIcon, TargetIcon, HeartIcon, ClockIcon, DollarSignIcon } from 'lucide-react';
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      delay: i * 0.15,
      ease: [0.4, 0, 0.2, 1]
    }
  })
};
export function SystemReveal() {
  return <section className="bg-light py-20 md:py-32 px-5" data-id="element-256">
      <div className="max-w-wide mx-auto" data-id="element-257">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true,
        margin: '-80px'
      }} data-id="element-258">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-dark leading-tight mb-4" data-id="element-259">
            El Sistema de las{' '}
            <span className="text-gradient-money" data-id="element-260">3 Jarras</span>
          </h2>
          <p className="font-sans text-dark/50 text-lg max-w-lg mx-auto" data-id="element-261">
            Simple a propósito. Lo que funciona en familias ocupadas no es lo
            complejo… es lo repetible.
          </p>
        </motion.div>

        {/* Three Jar Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-262">
          {/* Gastar */}
          <motion.div className="bg-white rounded-2xl shadow-xl overflow-hidden" custom={0} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-263">
            <div className="p-8 text-center" data-id="element-264">
              <div className="w-20 h-20 rounded-full bg-spend/10 flex items-center justify-center mx-auto mb-5" data-id="element-265">
                <ShoppingBagIcon className="w-9 h-9 text-spend" data-id="element-266" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-1" data-id="element-267">
                <DollarSignIcon className="w-5 h-5 text-spend/50" data-id="element-268" />
                <h3 className="font-display text-2xl font-extrabold text-spend" data-id="element-269">
                  Gastar
                </h3>
              </div>
              <p className="text-xs font-display font-semibold text-dark/30 uppercase tracking-widest mb-4" data-id="element-270">
                Spend
              </p>
              <p className="font-sans text-dark/60 leading-relaxed" data-id="element-271">
                Libertad con límites.
              </p>
            </div>
            <div className="h-2 bg-spend" data-id="element-272" />
          </motion.div>

          {/* Ahorrar */}
          <motion.div className="bg-white rounded-2xl shadow-xl overflow-hidden" custom={1} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-273">
            <div className="p-8 text-center" data-id="element-274">
              <div className="w-20 h-20 rounded-full bg-save/10 flex items-center justify-center mx-auto mb-5" data-id="element-275">
                <TargetIcon className="w-9 h-9 text-save" data-id="element-276" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-1" data-id="element-277">
                <DollarSignIcon className="w-5 h-5 text-save/50" data-id="element-278" />
                <h3 className="font-display text-2xl font-extrabold text-save" data-id="element-279">
                  Ahorrar
                </h3>
              </div>
              <p className="text-xs font-display font-semibold text-dark/30 uppercase tracking-widest mb-4" data-id="element-280">
                Save
              </p>
              <p className="font-sans text-dark/60 leading-relaxed" data-id="element-281">
                Paciencia entrenable y metas.
              </p>
            </div>
            <div className="h-2 bg-save" data-id="element-282" />
          </motion.div>

          {/* Dar */}
          <motion.div className="bg-white rounded-2xl shadow-xl overflow-hidden" custom={2} variants={cardVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-283">
            <div className="p-8 text-center" data-id="element-284">
              <div className="w-20 h-20 rounded-full bg-give/10 flex items-center justify-center mx-auto mb-5" data-id="element-285">
                <HeartIcon className="w-9 h-9 text-give" data-id="element-286" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-1" data-id="element-287">
                <DollarSignIcon className="w-5 h-5 text-give/50" data-id="element-288" />
                <h3 className="font-display text-2xl font-extrabold text-give" data-id="element-289">
                  Dar
                </h3>
              </div>
              <p className="text-xs font-display font-semibold text-dark/30 uppercase tracking-widest mb-4" data-id="element-290">
                Give
              </p>
              <p className="font-sans text-dark/60 leading-relaxed" data-id="element-291">
                Valores prácticos, sin moralina.
              </p>
            </div>
            <div className="h-2 bg-give" data-id="element-292" />
          </motion.div>
        </div>

        {/* 7-Minute Meeting */}
        <motion.div className="max-w-2xl mx-auto" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true,
        margin: '-60px'
      }} data-id="element-293">
          <div className="text-center mb-8" data-id="element-294">
            <span className="inline-flex items-center gap-2 text-xs font-display font-bold tracking-[0.15em] uppercase text-money-deep border border-money-deep/30 bg-money/5 rounded-full px-5 py-2 mb-5" data-id="element-295">
              <ClockIcon className="w-3.5 h-3.5" data-id="element-296" />
              Cada Semana
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-dark" data-id="element-297">
              La Reunión de{' '}
              <span className="text-gradient-money" data-id="element-298">7 Minutos</span>
            </h3>
            <p className="font-sans text-dark/40 text-sm mt-2" data-id="element-299">
              Un guion exacto. Sin pantallas. Sin debate.
            </p>
          </div>

          {/* Key phrase card */}
          <div className="bg-dark rounded-2xl border border-money/25 p-8 md:p-10 text-center money-glow mb-8" data-id="element-300">
            <p className="font-display text-2xl md:text-3xl font-extrabold text-gradient-money leading-snug" data-id="element-301">
              «Puede ser. ¿De qué jarra saldría?»
            </p>
          </div>

          <p className="font-sans text-dark/50 text-center text-lg" data-id="element-302">
            Ya no es <span className="italic" data-id="element-303">«papá malo / mamá mala»</span>.
          </p>
          <p className="font-display text-2xl font-bold text-dark text-center mt-2" data-id="element-304">
            Es el sistema.
          </p>

          {/* Mid CTA */}
          <div className="text-center mt-10" data-id="element-305">
            <a href="#precio" className="inline-block bg-gradient-to-r from-money-dark via-money to-emerald-400 text-white font-display font-bold text-base px-8 py-3.5 rounded-xl shadow-lg hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-money" data-id="element-306">
              Quiero Este Sistema →
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}